import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function SubscriptionSuccessPage() {
    const { token } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('checking'); // checking, success, error
    const [attempts, setAttempts] = useState(0);

    const sessionId = searchParams.get('session_id');
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        if (sessionId && token) {
            pollPaymentStatus();
        } else if (!sessionId) {
            setStatus('error');
        }
    }, [sessionId, token]);

    const pollPaymentStatus = async () => {
        const maxAttempts = 10;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            setStatus('error');
            return;
        }

        try {
            const response = await fetch(`${API}/subscription/checkout-status/${sessionId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.payment_status === 'paid' || data.status === 'complete') {
                    setStatus('success');
                    return;
                } else if (data.status === 'expired') {
                    setStatus('error');
                    return;
                }
            }

            // Continue polling
            setAttempts(prev => prev + 1);
            setTimeout(pollPaymentStatus, pollInterval);
        } catch (error) {
            console.error('Error checking payment status:', error);
            setAttempts(prev => prev + 1);
            setTimeout(pollPaymentStatus, pollInterval);
        }
    };

    return (
        <Layout>
            <div className="max-w-lg mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-8 text-center"
                >
                    {status === 'checking' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
                                <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">
                                Processing Your Subscription...
                            </h1>
                            <p className="text-[#A3A3A3]">
                                Please wait while we confirm your payment.
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-10 h-10 text-green-400" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">
                                Welcome to Pitch Room Pro!
                            </h1>
                            <p className="text-[#A3A3A3] mb-6">
                                Your subscription is now active. You have full access to all features.
                            </p>
                            <div className="space-y-3">
                                <Button
                                    onClick={() => navigate('/poster/dashboard')}
                                    className="w-full bg-[#F51E1E] hover:bg-[#D41212] text-white"
                                >
                                    Go to Dashboard
                                </Button>
                                <Button
                                    onClick={() => navigate('/create-project')}
                                    variant="outline"
                                    className="w-full border-[#444] text-white hover:bg-[#262626]"
                                >
                                    Create Your First Project
                                </Button>
                            </div>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                                <XCircle className="w-10 h-10 text-red-400" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">
                                Something Went Wrong
                            </h1>
                            <p className="text-[#A3A3A3] mb-6">
                                We couldn't confirm your subscription. If you were charged, please contact support.
                            </p>
                            <div className="space-y-3">
                                <Button
                                    onClick={() => navigate('/pricing')}
                                    className="w-full bg-[#F51E1E] hover:bg-[#D41212] text-white"
                                >
                                    Try Again
                                </Button>
                                <Button
                                    onClick={() => window.location.href = 'mailto:support@pitchroom.com'}
                                    variant="outline"
                                    className="w-full border-[#444] text-white hover:bg-[#262626]"
                                >
                                    Contact Support
                                </Button>
                            </div>
                        </>
                    )}
                </motion.div>
            </div>
        </Layout>
    );
}
